import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useErrorContext } from '@qpr/client-library';
import { makePalette } from 'material-color-tool';
import React, { useEffect, useState } from 'react';

const PREFIX = 'MultiListWidget';

const classes = {
  bgHover: `${PREFIX}-bgHover`,
  formLabelRoot: `${PREFIX}-formLabelRoot`,
  chip: `${PREFIX}-chip`
};

const Root = styled('div')(({ theme }) => {
  const shadePalette = makePalette(theme.palette.secondary.main);

  return {
    [`& .${classes.bgHover}`]: {
      '&:hover': {
        background: theme.palette.primary.light
      }
    },
    [`& .${classes.formLabelRoot}`]: {
      color: alpha(theme.palette.grey[900], 0.62)
    },
    [`& .${classes.chip}`]: {
      borderRadius: '4px',
      backgroundImage: `linear-gradient(0deg, ${alpha(shadePalette[4], 0.12)}, ${alpha(
        shadePalette[4],
        0.12
      )}), linear-gradient(0deg, ${theme.palette.background.paper}, ${
        theme.palette.background.paper
      })`,
      '&:hover': {
        transition: 'background-color 0ms',
        backgroundBlendMode: 'multiply',
        backgroundColor: alpha(theme.palette.grey[900], 0.04)
      }
    }
  };
});

const MultiListWidget = ({ setValue, value, field, customProps }: any) => {
  const [options, setOptions] = useState<any[] | undefined>(undefined);
  const onChangeHandler = (event: React.ChangeEvent<{}>, newValue: any) => {
    setValue(newValue.map((option: any) => option.value) || []);
  };
  const { setError } = useErrorContext();

  useEffect(() => {
    const initData = async () => {
      const gotOptions = await customProps.dataFetch(field);
      setOptions(gotOptions.data);
      if (gotOptions.error?.message) {
        setError(gotOptions.error);
      }
    };

    initData();
  }, []);

  const chosenValues =
    options?.filter((obj: any) => {
      return value?.includes(obj.value);
    }) || [];

  /*
    THIS SHOULD BE AS PROP IN AUTOCOMPLETE
 ListboxProps={{ 'data-testid': 'listbox' }}
    */

  return options !== undefined ? (
    <Root>
      <Autocomplete
        multiple
        disabled={options.length === 0}
        className={classes.bgHover}
        classes={{
          root: classes.bgHover
        }}
        id={field}
        options={options || []}
        getOptionLabel={option => option.title}
        renderOption={(props, option) => (
          <span {...props} data-testid={option.id}>
            {option.title}
          </span>
        )}
        value={chosenValues}
        onChange={onChangeHandler}
        ListboxProps={{ id: 'listbox' }}
        renderInput={params => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              'data-testid': `multiselect-widget-${field}-value`
            }}
            fullWidth
            InputLabelProps={{
              style: { fontWeight: 'normal' },
              classes: {
                root: classes.formLabelRoot
              }
            }}
            label="Select value(s)"
            variant="outlined"
          />
        )}
        ChipProps={{
          classes: {
            root: classes.chip
          }
        }}
      />
    </Root>
  ) : (
    <div
      style={{ height: '56px', display: 'inline-flex', verticalAlign: 'top', alignItems: 'center' }}
    >
      <CircularProgress />
    </div>
  );
};

export default MultiListWidget;
